import { Label } from "@/components/atoms/Label/label"
import { HeadingText } from "@/components/atoms/HeadingText/heading-text"

type ContactDetailItemProps = {
  icon: React.ReactNode
  label: string
  value?: string
  /** @deprecated Resolution is automatic from value shape; kept for backward compatibility. */
  isLink?: boolean
}

/** Trim and normalize internal whitespace without breaking intentional single spaces in display text. */
export function sanitizeContactValue(raw: string | undefined): string {
  if (raw == null) return ""
  return raw.trim().replace(/\s+/g, " ")
}

function looksLikeEmail(v: string): boolean {
  if (!v.includes("@")) return false
  const [local, domain] = v.split("@")
  if (!local?.length || !domain?.includes(".")) return false
  return !/\s/.test(v)
}

function looksLikePhone(v: string): boolean {
  if (v.includes("@")) return false
  const digits = v.replace(/\D/g, "")
  if (digits.length < 7 || digits.length > 15) return false
  return /^[\d\s\-+().xext]+$/i.test(v)
}

function looksLikeInternalPath(v: string): boolean {
  return v.startsWith("/") && !v.startsWith("//")
}

/**
 * Returns a safe href for contact fields, or null when the value should render as plain text.
 * Exported for CTAs and other components that need the same rules as ContactDetailItem.
 */
export function resolveContactHref(raw: string | undefined): string | null {
  const v = sanitizeContactValue(raw ?? "")
  if (!v) return null

  if (looksLikeEmail(v)) {
    const addr = v.replace(/^mailto:/i, "").trim()
    return `mailto:${addr}`
  }

  if (looksLikeInternalPath(v)) {
    return v
  }

  if (looksLikePhone(v)) {
    const digits = v.replace(/\D/g, "")
    return `tel:${digits}`
  }

  // Explicit schemes
  if (/^https?:\/\//i.test(v)) {
    try {
      const u = new URL(v)
      if (u.protocol === "http:" || u.protocol === "https:") return u.href
    } catch {
      return null
    }
    return null
  }

  if (/^www\./i.test(v)) {
    const candidate = `https://${v}`
    try {
      return new URL(candidate).href
    } catch {
      return null
    }
  }

  // Domain-like (e.g. swissport.com/careers) — avoid treating "St. Louis, MO" as URL (spaces / commas)
  if (!/\s/.test(v) && /[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}/.test(v)) {
    const candidate = `https://${v}`
    try {
      return new URL(candidate).href
    } catch {
      return null
    }
  }

  return null
}

function linkProps(href: string) {
  const isHttp = href.startsWith("http://") || href.startsWith("https://")
  const isPath = href.startsWith("/")
  if (isHttp || isPath) {
    return { target: "_blank" as const, rel: "noopener noreferrer" as const }
  }
  return {}
}

export const ContactDetailItem = ({ icon, label, value }: ContactDetailItemProps) => {
  const display = sanitizeContactValue(value)
  const href = resolveContactHref(display)

  return (
    <div className="flex items-center space-x-3 py-2">
      <span className="text-blue-500">{icon}</span>
      <div className="flex flex-col">
        <Label>{label}</Label>
        <HeadingText level={5} className="w-56 overflow-hidden break-words line-clamp-3">
          {!display ? null : href ? (
            <a href={href} className="text-inherit underline hover:opacity-90" {...linkProps(href)}>
              {display}
            </a>
          ) : (
            display
          )}
        </HeadingText>
      </div>
    </div>
  )
}
